import React, { useState, useEffect, useRef } from 'react';
import { ResizeObserver as ResizeObserverPolyfill } from '@juggle/resize-observer';
import { connect, useDispatch, useSelector } from 'react-redux';
import { ReduxState } from 'services/types';
import { Theme, Snackbar, IconButton, Card, CardContent, Typography } from '@mui/material';
import { WithStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import CloseIcon from '@mui/icons-material/Close';
import {
	Shopper,
	OrderProduct,
	ProductApi,
	Category,
	Shop,
	Languages,
	ShopPublicInfo,
	OrderInfo,
	OrderObject,
	MatchedProduct,
	SetProductWithParentId,
	Discount,
} from 'common/types';
import ShopperProducts from './ShopperProducts';
import Grid from '@mui/material/Grid';
import ReactToPrint from 'react-to-print';
import ShopperReceipt from 'components/ShopperReceipt';
import { getProductCodes } from 'services/utils';
import {
	getStockProductIdsFromProducts,
	getCategoryIdsFromShoppers,
	getShopperIdsWithProductsFromShoppers,
	getUpdatedShoppersForReturning,
	getUpdatedProductsForProductReturn,
	getUpdatedRentalInfoForProductReturn,
	getUniqProductTypes,
} from 'common/utils/rentalUtils';
import {
	getOrderProductWithScannedId,
	doesShopperStillHaveProductsWithoutCode,
	getUpdatedPartialProductsOnCodeChange,
	getShopperCardOverlayHeight,
} from './utils';
import {
	fetchScannedStockItemFromDb,
	getMatchedOrderProductsFromActiveShopper,
	getUpdatedRentalInfoFromShopperToDelete,
} from '../utils';
import AddScannedProductDialog from './components/AddScannedProductDialog';
import DeleteShopperDialog from './components/DeleteShopperDialog';
import {
	addOrderPaymentRequest,
	updateOrderStateHistory,
	getNewPaymentNumber,
} from 'common/api/frontend';
import DiscountDialog from 'components/DiscountDialog';
import { getCurrencyObjectFromCurrencyCode } from 'common/utils/currencyUtils';
import ShopperInfo from './ShopperInfo';
import { useTranslation } from 'services/localization';
import { getNewOrderNumber } from 'common/api/db/functions';
import { getResponsiblePersonDetails, newFirestoreId } from 'common/utils/newRentalUtils';
import {
	addDiscountToProducts,
	getOrderPricing,
	getPricingOptions,
	getTotalProductPricing,
	getOrderProductSummary,
	isSalesProduct,
} from 'common/modules/orders';
import { getExtendedItemPricing } from 'common/modules/atoms/pricing';
import { getItemsPayments } from 'common/modules/atoms/paymentAmounts';
import classNames from 'classnames';
import ShopperCommentModal from 'components/ShopperCommentModal';
import { InventoryItem, InventoryItemController, isSingleItem } from 'common/modules/inventory';
import { getProductsBySkuId } from 'common/modules/products/utils';
import { getProductVariants, getVariantsBySkuId } from 'common/modules/products/variants';
import errorHandler from 'common/services/errorHandler';
import { getDefaultOrderBarcode } from 'common/modules/orderReceipts/utils';
import * as StockSelectors from 'selectors/StockSelectors';
import {
	getExternalPosTriggerTypes,
	handlePosIntegrationAction,
} from 'common/modules/posIntegrations';
import { getReceiptBarCode } from 'services/utils/receipt';
import { api } from 'common/frontend/api';
import { notFalsey, notUndefined } from 'common/utils/common';
import * as RentalViewActions from 'actions/RentalViewActions';
import * as NotificationActions from 'actions/NotificationActions';
interface OwnProps {
	rental: OrderObject;
	shopper: Shopper;
	shoppers: Shopper[];
	shopperProducts: OrderProduct[];
	shopperIndex: number;
	allOrderProducts: OrderProduct[];
	shopPublicInfo: ShopPublicInfo;
	scanObject: { scannedCode: string };
	remeasureVirtualizedRow: () => void;
}

interface SnackBarFeedback {
	message: string;
	key: number;
}

interface StateToProps {
	stockProducts: ProductApi[];
	categories: Category[];
	shop: Shop;
	lang: Languages;
	activeShopperId: string | null;
	selectedNonSetProductIds: string[];
	selectedSetProducts: SetProductWithParentId[];
}

type Props = OwnProps & StateToProps & WithStyles<typeof styles>;

export const ShopperCard = (props: Props) => {
	const {
		shopper,
		shopperProducts,
		classes,
		shop,
		rental,
		shopperIndex,
		lang,
		shopPublicInfo,
		scanObject,
		stockProducts,
		shoppers,
		allOrderProducts,
		activeShopperId,
		remeasureVirtualizedRow,
	} = props;
	const dispatch = useDispatch();
	const shopperCardRef = useRef<HTMLDivElement | null>(null);
	const { rentalInfo, orderDelivery } = props.rental;
	const { id: orderId, orderNumber } = rentalInfo;
	const { readOnly, shopInfo } = shop;
	const queueRef = React.useRef<SnackBarFeedback[]>([]);
	const chargePayment = getItemsPayments(shopperProducts).charge;
	const categories = useSelector(StockSelectors.categoriesData);
	const toPay = chargePayment.unpaid;
	const [deleteShopperPrompt, setDeleteShopperPrompt] = useState(false);
	const [snackBarFeedback, setSnackBarFeedback] = useState<SnackBarFeedback | undefined>(undefined);
	const [snackBarOpen, setSnackBarOpen] = useState<boolean>(false);
	const [productToSetHighlightFor, setProductToSetHighlightFor] = useState<MatchedProduct | null>(
		null,
	);
	const [addScannedProductDialogOpen, setAddScannedProductDialogOpen] = useState<boolean>(false);
	const [scannedItem, setScannedItem] = useState<InventoryItem | null>(null);
	const [discountDialogOpen, setDiscountDialogOpen] = useState<boolean>(false);
	const [additionalInformationDialogOpen, setAdditionalInformationDialogOpen] = useState(false);
	const currency = getCurrencyObjectFromCurrencyCode(
		rental.rentalInfo.pricing.currency,
		shopPublicInfo.currency,
	);
	const [printingShopperId, setPrintingShopperId] = useState<string | null>(null);
	const [paymentNumber, setPaymentNumber] = useState<number | null>(null);
	const receiptRef = useRef<HTMLDivElement | null>(null);
	const { t } = useTranslation();
	const [printContentPromise, setPrintContentPromise] = useState<{ resolve?: () => void }>({});
	const [receiptBarCode, setReceiptBarCode] = useState<string | null>(null);
	const [editingShopper, setEditingShopper] = useState<boolean>(false);
	const [editingPrice, setEditingPrice] = useState<boolean>(false);
	const activeShopper = props.activeShopperId === shopper.id;
	const externalPosTriggerTypes = getExternalPosTriggerTypes({
		readOnly,
		shopInfo,
	});
	const onShopperReceiptPrintPosTriggerInUse = externalPosTriggerTypes.includes(
		'onShopperReceiptPrint',
	);

	const editingCard = editingShopper || editingPrice;

	useEffect(() => {
		const shopperCardElement = shopperCardRef.current;
		let elementCurrentHeight = shopperCardElement?.clientHeight ?? -1;
		const ResizeObserver = ResizeObserverPolyfill;
		const resizeObserver = new ResizeObserver((entries) => {
			const newElementHeight = Math.round(entries[0].contentRect.height);
			if (!newElementHeight) {
				resizeObserver.disconnect();
				return;
			}
			const heightUpdated = Math.abs(elementCurrentHeight - newElementHeight) > 5;
			if (!!shopperCardElement && heightUpdated) {
				try {
					elementCurrentHeight = newElementHeight;
					remeasureVirtualizedRow();
				} catch (e) {}
			}
		});
		if (!!shopperCardElement) {
			resizeObserver.observe(shopperCardElement);
		}
		return () => {
			resizeObserver.disconnect();
		};
	}, [remeasureVirtualizedRow]);

	useEffect(() => {
		const printButton = document.getElementById(`print-btn-${printingShopperId}`);
		if (printingShopperId && printButton) {
			printButton.click();
		}
	}, [printingShopperId]);

	// This is to make sure all relevant content is loaded before we trigger receipt printing
	useEffect(() => {
		if (
			printContentPromise.resolve &&
			receiptBarCode != null &&
			paymentNumber != null &&
			orderNumber != null
		) {
			printContentPromise.resolve && printContentPromise.resolve();
			setPrintContentPromise({});
		}
	}, [printContentPromise, receiptBarCode, paymentNumber, orderNumber]);

	useEffect(() => {
		if (scanObject.scannedCode && !addScannedProductDialogOpen) {
			handleBarCodeRead(scanObject.scannedCode);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [scanObject]);

	const handleCloseSnackBar = () => {
		setSnackBarOpen(false);
	};

	const handleExited = () => {
		processQueue();
	};

	const processQueue = () => {
		if (queueRef.current && queueRef.current.length > 0) {
			setSnackBarFeedback(queueRef.current.shift());
			setSnackBarOpen(true);
		}
	};

	const handleGiveFeedback = (message: string) => {
		queueRef.current.push({
			message,
			key: new Date().getTime(),
		});

		if (snackBarOpen) {
			setSnackBarOpen(false);
		} else {
			processQueue();
		}
	};

	const handleBarCodeRead = async (scannedId: string) => {
		// CASE 0.1 - check if scanned code is already added to any product in the current order.
		const { productWithScannedId, shopperId } = getOrderProductWithScannedId(
			rental.products,
			scannedId,
		);
		if (productWithScannedId && shopperId) {
			dispatch(RentalViewActions.updateActiveShopperId(shopperId));
			updateSelectedProducts(productWithScannedId);
			// TODO -> scroll to newly highlighted product?.
			return;
		} else if (shopper.id === activeShopperId) {
			// CASE 1 - scanned code matches a stockItem in the shops inventory.
			const scannedItem = await fetchScannedStockItemFromDb(scannedId, props.shopPublicInfo.shopId);
			const singleItem = !!scannedItem && !!isSingleItem(scannedItem);
			if (scannedItem && singleItem) {
				setScannedItem(scannedItem);
				const scannedItemSkuId = scannedItem.skuId;
				const validProducts = !scannedItemSkuId
					? []
					: getProductsBySkuId(stockProducts, scannedItemSkuId);
				// CASE 1.1 - current shopper has the scanned stockItem as a orderProduct.
				const matchedProductsFromActiveShopper = getMatchedOrderProductsFromActiveShopper(
					validProducts.map((p) => p.id),
					activeShopperId,
					rental.products,
				);
				if (matchedProductsFromActiveShopper.length) {
					handleNewScannedItemForShopper(scannedId, matchedProductsFromActiveShopper, scannedItem);
					return;
				}
				// CASE 1.2 - scanned item is a sales product which cannot be added to the order at this time (12/08/2022) - Axel Cedercreutz
				const salesItem = notFalsey(scannedItem.allocation.sales);
				if (salesItem) {
					handleGiveFeedback(
						t('scanningFeedback.salesProductCouldNotBeAdded', {
							scannedId: scannedId,
							defaultValue: 'Sales product with scanned id: {{scannedId}} could not be added',
						}) as string,
					);
					return;
				}
				// CASE 1.3 - (TODO) current shopper does not have the scanned stockItem as a orderProduct, so lets add it.
				// - if this stockItem belongs to a set, ask user to add whole set or only the single item.
				setAddScannedProductDialogOpen(true);
				return;
			}

			// CASE 2 - scannedCode matches a bulkItem in the shops inventory -> user will be asked if one should be added.
			if (scannedItem && !singleItem) {
				// CASE 2.1 - scanned item is not a rental product which cannot be added to the order at this time (12/08/2022) - Axel Cedercreutz
				const rentalItem = notFalsey(scannedItem.allocations.rental);
				if (!rentalItem) {
					handleGiveFeedback(
						t('scanningFeedback.salesProductCouldNotBeAdded', {
							scannedId: scannedId,
							defaultValue: 'Sales product with scanned id: {{scannedId}} could not be added',
						}) as string,
					);
					return;
				}
				setScannedItem(scannedItem);
				setAddScannedProductDialogOpen(true);
				return;
			}

			// CASE 3 - scanned code does not match a stockItem in the shops inventory,
			/*
            - ask user if they want to add the code to one of the existing products.
            - Or if they want to create a "custom product" from it.
            */
			handleGiveFeedback(
				t('scanningFeedback.notFound', {
					scannedId: scannedId,
					defaultValue: 'No product found from inventory with scanned id: {{scannedId}}',
				}) as string,
			);
			return;
		}
	};

	const updateSelectedProducts = (matchedProductWithScannedCode: MatchedProduct) => {
		// CASE 1.1 -> product to select is set.
		if (matchedProductWithScannedCode.setIndex != null) {
			const setParentToSelectFrom = allOrderProducts.find(
				(p) => p.id === matchedProductWithScannedCode.orderProductId,
			);
			const setProductToSelect = setParentToSelectFrom
				? setParentToSelectFrom.setProducts.find(
						(setP, index) => index === matchedProductWithScannedCode.setIndex,
				  )
				: null;
			if (setProductToSelect) {
				const setProductToSelectWithParentId: SetProductWithParentId = {
					...setProductToSelect,
					parentId: matchedProductWithScannedCode.orderProductId,
				};
				if (
					!props.selectedSetProducts.find(
						(setP) =>
							setP.parentId === setProductToSelectWithParentId.parentId &&
							setP.productApiId === setProductToSelectWithParentId.productApiId,
					)
				) {
					const updatedSelectedSetProducts = [
						...props.selectedSetProducts,
						setProductToSelectWithParentId,
					];
					dispatch(RentalViewActions.updateSelectedSetProducts(updatedSelectedSetProducts));
				}
			}
			return;
		}
		// CASE 1.2 -> product to select is nonSet.
		const productToSelect = allOrderProducts.find(
			(p) => p.id === matchedProductWithScannedCode.orderProductId,
		);
		if (productToSelect && !props.selectedNonSetProductIds.includes(productToSelect.id)) {
			const updatedSelectedNonSetProductIds = [
				...props.selectedNonSetProductIds,
				productToSelect.id,
			];
			dispatch(RentalViewActions.updateSelectedNonSetProductIds(updatedSelectedNonSetProductIds));
		}
	};

	const handleNewScannedItemForShopper = (
		scannedId: string,
		matchedProductsFromScan: MatchedProduct[],
		matchedStockItemFromScan: InventoryItem,
	) => {
		// CASE 1 - the scanned code is already added to a product, so the product should be selected.
		const matchedProductWithScannedCode = matchedProductsFromScan.find(
			(p) => !!p.productCode && p.productCode === scanObject.scannedCode,
		);
		if (matchedProductWithScannedCode) {
			updateSelectedProducts(matchedProductWithScannedCode);
			return;
		}

		// CASE 2 - the shopper has the same products without a code yet as the scanned product, so code will try to be added.
		const matchedProductsWithoutCode = matchedProductsFromScan.filter((p) => !p.productCode);
		if (matchedProductsWithoutCode.length && matchedStockItemFromScan) {
			addCodeForCorrectProduct(scannedId, matchedProductsWithoutCode, matchedStockItemFromScan);
			return;
		}

		// CASE 3 - user still has products to scan but all of productType from the current scan already have a code.
		if (doesShopperStillHaveProductsWithoutCode(shopperProducts) && matchedStockItemFromScan) {
			setAddScannedProductDialogOpen(true);
			return;
		}

		// CASE 4 - All the shoppers products which match the scanned product already have codes selected, so a new one will be added.
		handleGiveFeedback(
			t('scanningFeedback.allProductsHaveAnId', {
				firstName: shopper.firstName,
				defaultValue: 'All products for {{firstName}} have an ID already.',
			}) as string,
		);
		return;
	};

	const addCodeForProduct = (
		scannedId: string,
		matchedProductOnScan: MatchedProduct,
		scannedStockItem: InventoryItem,
	) => {
		const { orderProductId, setIndex, selectedVariant } = matchedProductOnScan;
		const stockProduct = stockProducts.find(
			(stockP) => stockP.id === matchedProductOnScan.productApiId,
		);
		if (!stockProduct) return;
		const variantId = !scannedStockItem.skuId
			? undefined
			: getVariantsBySkuId(stockProduct.variants.options, scannedStockItem.skuId)?.[0]?.id;
		const updatedPartialProducts = getUpdatedPartialProductsOnCodeChange({
			code: scannedId,
			orderProductId,
			setIndex,
			currentVariant: selectedVariant,
			newVariantId: variantId,
			selectedStockItem: scannedStockItem,
			stockProduct,
			shopperProducts,
			useDinVariation: !!props.shop.shopInfo.plugins?.din?.useDinVariation,
			userProperties: shopper.userProperties,
			t,
			dinCollectionMethod: shop.shopInfo.dinCollectionMethod ?? null,
		});
		const updatedOrderProducts = props.rental.products.map((p) => {
			const updatedPartial = updatedPartialProducts.find((updP) => updP.id === p.id);
			if (updatedPartial) {
				return {
					...p,
					...updatedPartial.data,
				};
			}
			return p;
		});
		dispatch(
			RentalViewActions.updateActivePartialOrder({
				products: updatedPartialProducts,
				rentalInfo: {
					id: rental.rentalInfo.id,
					data: {
						rentalProductCodes: getProductCodes(updatedOrderProducts),
					},
				},
			}),
		);

		setProductToSetHighlightFor(matchedProductOnScan);
		// TODO -> handle case where no matched product, should not happen.
	};

	const addCodeForCorrectProduct = (
		scannedId: string,
		matchedProductsWithoutCodeForShopper: MatchedProduct[],
		matchedStockItemFromScan: InventoryItem,
	) => {
		const scannedSkuId = matchedStockItemFromScan.skuId;
		if (!scannedSkuId) {
			errorHandler.report(`Adding code for item with no sku ID: ${matchedStockItemFromScan.id}`);
			handleGiveFeedback(
				t('scanningFeedback.notFound', {
					scannedId: scannedId,
					defaultValue: 'No product found from inventory with scanned id: {{scannedId}}',
				}) as string,
			);
			return;
		}
		const validProducts = getProductsBySkuId(stockProducts, scannedSkuId);
		const validVariants = getVariantsBySkuId(
			validProducts.flatMap((p) => getProductVariants(p)),
			scannedSkuId,
		);
		const validVariantIds = validVariants.map((v) => v.id);
		// IN ALL THE FOLLOWING CASES THE SHOPPER HAS PRODUCTS THAT WAS SCANNED WITHOUT A CODE -> VARIANTS WILL BE CHECKED NOW.

		// CASE 1 - shopper has scanned products without a variant selected. (self-service rentals)
		const matchedProductsWithoutVariantSelected = matchedProductsWithoutCodeForShopper.filter(
			(p) => !p.selectedVariant,
		);
		if (matchedProductsWithoutVariantSelected.length) {
			const matchedProductOnScan = matchedProductsWithoutVariantSelected[0];
			addCodeForProduct(scannedId, matchedProductOnScan, matchedStockItemFromScan);
			return;
		}

		// CASE 2 - shopper has scanned products with same variant
		const matchedProductsWithSameVariantAsScannedStockItem = matchedProductsWithoutCodeForShopper.filter(
			(p) => p.selectedVariant && validVariantIds.includes(p.selectedVariant.id),
		);
		if (matchedProductsWithSameVariantAsScannedStockItem.length) {
			const matchedProductOnScan = matchedProductsWithSameVariantAsScannedStockItem[0];
			addCodeForProduct(scannedId, matchedProductOnScan, matchedStockItemFromScan);
			return;
		}

		// CASE 3 - shopper only has different variants of the same product that was scanned
		const matchedProductsWithDifferentVariantAsScannedStockItem = matchedProductsWithoutCodeForShopper.filter(
			(p) => p.selectedVariant && validVariantIds.includes(p.selectedVariant.id),
		);
		if (matchedProductsWithDifferentVariantAsScannedStockItem.length && matchedStockItemFromScan) {
			// TODO -> ask user to either change variant for existing products or add new with current variant.
			// const variantName = getVariantName(matchedStockItemFromScan.variantValues, lang)
			handleGiveFeedback(
				t('scanningFeedback.noProductWithSameVariant', {
					stockItem: matchedStockItemFromScan.skuCode,
					firstName: shopper.firstName,
					defaultValue:
						'Scanned product: {{stockItem}} but {{firstName}} does not have product with this variant',
				}),
			);
			return;
		}
	};

	const getUpdatedProductListsForDeleting = (
		productIdsToDelete: string[],
		setProductsToDelete: SetProductWithParentId[],
	) => {
		let updatedProducts: OrderProduct[] = [];

		const updatedProductIdsToDelete: string[] = [...productIdsToDelete];
		// if any set products are included.
		if (setProductsToDelete.length) {
			rental.products.forEach((product) => {
				const setProductsToDeleteForCurrentProduct = setProductsToDelete.filter(
					(setP) => setP.parentId === product.id,
				);
				const nonRemovedProductSetProducts = product.setProducts.filter(
					(setP) => !setP.removedFromParent,
				);
				const allSetProductsRemoved =
					setProductsToDeleteForCurrentProduct.length === nonRemovedProductSetProducts.length;
				if (productIdsToDelete.includes(product.id)) return;
				if (!setProductsToDeleteForCurrentProduct.length) {
					updatedProducts.push(product);
					return;
				}
				if (!!product.setProducts.length && allSetProductsRemoved) {
					updatedProductIdsToDelete.push(product.id);
					return;
				}
				if (!!setProductsToDeleteForCurrentProduct && !allSetProductsRemoved) {
					const updatedSetProducts = product.setProducts.map((setP) => {
						if (
							setProductsToDeleteForCurrentProduct.find(
								(setPToDelete) => setPToDelete.productApiId === setP.productApiId,
							)
						) {
							return {
								...setP,
								removedFromParent: true,
							};
						}
						return setP;
					});
					const updatedProductApiIds = product.productApiIds.filter(
						(id) =>
							!setProductsToDeleteForCurrentProduct.find(
								(setPToDelete) => setPToDelete.productApiId === id,
							),
					);

					const newSummary = getOrderProductSummary({
						...product,
						setProducts: updatedSetProducts,
					});

					const updatedProduct = {
						...product,
						productApiIds: updatedProductApiIds,
						includedVariantIds: newSummary.skuIds,
						summary: newSummary,
						setProducts: updatedSetProducts,
					};
					updatedProducts.push(updatedProduct);
					return;
				}
				updatedProducts.push(product);
			});
		} else {
			updatedProducts = rental.products.filter((p) => !productIdsToDelete.includes(p.id));
		}

		return { updatedProducts, updatedProductIdsToDelete };
	};

	const getUpdatedShoppersWithDeletedProducts = (updatedProductIdsToDelete: string[]) => {
		const updatedShoppers: Shopper[] = shoppers.map((s) => {
			if (s.id === shopper.id) {
				const updatedProductIdsForShopper = s.productIds.filter(
					(pId) => !updatedProductIdsToDelete.includes(pId),
				);
				const updatedProductsForShopper = rental.products.filter((p) =>
					updatedProductIdsForShopper.includes(p.id),
				);
				const updatedCategoryIdsForShopper = [
					...new Set(
						updatedProductsForShopper.flatMap((item) =>
							item.categoryIds && item.categoryIds.length ? item.categoryIds : [],
						),
					),
				];
				const updatedShopper: Shopper = {
					...s,
					productIds: updatedProductIdsForShopper,
					categoryIds: updatedCategoryIdsForShopper,
				};
				return updatedShopper;
			} else {
				return s;
			}
		});
		return updatedShoppers;
	};

	const handleReturnProducts = async (
		productIdsToReturn: string[],
		setProductsToReturn: SetProductWithParentId[],
	) => {
		const updatedProducts = getUpdatedProductsForProductReturn({
			productIdsToReturn,
			setProductsToReturn,
			products: allOrderProducts,
		});

		const updatedShoppers = getUpdatedShoppersForReturning(shoppers, updatedProducts.full);
		const updatedPartialRentalInfo = getUpdatedRentalInfoForProductReturn(updatedProducts.full);
		dispatch(
			RentalViewActions.updateActivePartialOrder({
				rentalInfo: {
					id: rentalInfo.id,
					data: updatedPartialRentalInfo,
				},
				products: updatedProducts.changes,
				shoppers: updatedShoppers.changes,
			}),
		);
		if (
			updatedPartialRentalInfo.activeState &&
			updatedPartialRentalInfo.activeState !== rentalInfo.activeState
		) {
			updateOrderStateHistory({
				orderId: rentalInfo.id,
				oldStates: {
					activeState: rentalInfo.activeState,
					rentalState: rentalInfo.rentalState,
				},
				newStates: {
					activeState: updatedPartialRentalInfo.activeState,
					...(updatedPartialRentalInfo.rentalState && {
						rentalState: updatedPartialRentalInfo.rentalState,
					}),
				},
			});
		}

		const salesProducts = updatedProducts.full.filter(isSalesProduct);

		const updateInventoryItems = async () => {
			try {
				const productsToReturn = salesProducts.filter((p) => productIdsToReturn.includes(p.id));

				const itemCodesToReturn = [
					...productsToReturn.flatMap((product) => product.productCode),
					...setProductsToReturn.flatMap((product) => product.productCode),
				].filter(notUndefined);

				dispatch(RentalViewActions.setHideProductCodeErrors(true));
				await InventoryItemController.handleInventoryItemStatusChanges(api(), {
					shopId: shop.readOnly?.shopId!,
					products: productsToReturn,
					itemCodes: itemCodesToReturn,
					currentStatus: 'SOLD',
					targetStatus: 'IN_USE',
				});
				dispatch(RentalViewActions.setHideProductCodeErrors(false));
			} catch (err) {
				errorHandler.report(err);
			}
		};

		updateInventoryItems();
	};

	const handleDeleteProducts = async (
		productIdsToDelete: string[],
		setProductsToDelete: SetProductWithParentId[],
	) => {
		const { updatedProducts, updatedProductIdsToDelete } = getUpdatedProductListsForDeleting(
			productIdsToDelete,
			setProductsToDelete,
		);
		const updatedShoppers = getUpdatedShoppersWithDeletedProducts(updatedProductIdsToDelete);
		const updatedCategoryIds = getCategoryIdsFromShoppers(updatedShoppers);
		const updatedShopperIdsWithProducts = getShopperIdsWithProductsFromShoppers(updatedShoppers);
		const updatedStockProductIds = getStockProductIdsFromProducts(updatedProducts);
		const orderPricing = getOrderPricing(
			updatedProducts,
			orderDelivery,
			getPricingOptions(rentalInfo.pricing),
		);
		const updatedRentalInfo: Partial<OrderInfo> = {
			categoryIds: updatedCategoryIds,
			stockProductIds: updatedStockProductIds,
			shopperIdsWithProducts: updatedShopperIdsWithProducts,
			rentalProductCodes: getProductCodes(updatedProducts),
			includedProductTypes: getUniqProductTypes(updatedProducts),
			pricing: orderPricing,
		};
		dispatch(
			RentalViewActions.updateActivePartialOrder({
				rentalInfo: {
					id: rentalInfo.id,
					data: updatedRentalInfo,
				},
				products: updatedProducts.map((p) => ({
					id: p.id,
					data: p,
				})),
				shoppers: updatedShoppers.map((s) => ({
					id: s.id,
					data: s,
				})),
				productIdsToDelete: updatedProductIdsToDelete,
			}),
		);
	};

	const handleCardClicked = () => {
		dispatch(RentalViewActions.updateActiveShopperId(shopper.id));
	};

	const handleDeleteShopper = async () => {
		setDeleteShopperPrompt(false);
		const shopperIdsToDelete = [shopper.id];
		const productIdsToDelete = shopper.productIds;
		const updatedRentalInfo = getUpdatedRentalInfoFromShopperToDelete(rental, shopper);
		dispatch(
			RentalViewActions.updateActivePartialOrder({
				rentalInfo: {
					id: rental.rentalInfo.id,
					data: updatedRentalInfo,
				},
				shopperIdsToDelete,
				productIdsToDelete,
			}),
		);

		dispatch(
			NotificationActions.showNotification({
				message: t('personDeleted', 'Person deleted'),
				variant: 'info',
			}),
		);
	};

	const cancelDeleteShopper = () => {
		setDeleteShopperPrompt(false);
	};

	const renderSnackBar = () => {
		return (
			<Snackbar
				key={snackBarFeedback ? snackBarFeedback.key : undefined}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'left',
				}}
				open={snackBarOpen}
				autoHideDuration={5000}
				classes={{
					root: classes.snackBarRoot,
				}}
				onClose={handleCloseSnackBar}
				ContentProps={{
					'aria-describedby': 'message-id',
				}}
				TransitionProps={{
					onExited: handleExited,
				}}
			>
				<div className={classes.snack}>
					<IconButton
						key="close"
						aria-label="close"
						color="inherit"
						className={classes.close}
						onClick={() => handleCloseSnackBar()}
						size="large"
					>
						<CloseIcon />
					</IconButton>
					<Typography variant="h5" align="center" data-qa="errorMessage">
						{snackBarFeedback ? snackBarFeedback.message : undefined}
					</Typography>
				</div>
			</Snackbar>
		);
	};

	const handleCloseScannedProductDialog = () => {
		setAddScannedProductDialogOpen(false);
		setScannedItem(null);
	};

	const handleDiscountDialogClose = async (discount: Discount) => {
		setDiscountDialogOpen(false);
		const updatedShopperProducts = addDiscountToProducts(shopperProducts, discount.amount || 0);
		const allOrderProducts = rental.products.map((p) => {
			return updatedShopperProducts.full.find((prod) => prod.id === p.id) ?? p;
		});
		const newOrderPricing = getOrderPricing(
			allOrderProducts,
			orderDelivery,
			getPricingOptions(rental.rentalInfo.pricing),
		);
		const newRentalInfo: Partial<OrderInfo> = {
			pricing: newOrderPricing,
		};
		dispatch(
			RentalViewActions.updateActivePartialOrder({
				rentalInfo: {
					id: rental.rentalInfo.id,
					data: newRentalInfo,
				},
				products: updatedShopperProducts.changes,
			}),
		);
	};

	const handleAdditionalInformationDialogClose = () => {
		setAdditionalInformationDialogOpen(false);
	};

	const handleAdditionalInformationFormChange = async (value: string) => {
		setAdditionalInformationDialogOpen(false);
		dispatch(
			RentalViewActions.updateActivePartialOrder({
				shoppers: [
					{
						id: shopper.id,
						data: {
							additionalInformation: value,
						},
					},
				],
			}),
		);
	};

	const renderDiscountDialog = () => {
		const shopperPricing = getExtendedItemPricing(getTotalProductPricing(shopperProducts));
		const discountAmount = shopperPricing.manualDiscount ?? 0;
		const discount: Discount = { amount: discountAmount };
		const originalDiscountPrice = shopperPricing.subtotalWithoutManualDiscount;
		return (
			<DiscountDialog
				open={discountDialogOpen}
				onClose={handleDiscountDialogClose}
				discount={discount}
				originalPrice={originalDiscountPrice}
				currency={currency}
				shopperSpecific={shopper}
			/>
		);
	};

	const handlePrintingDone = (receiptBarCode: string | null, paymentNumber: number) => {
		if (onShopperReceiptPrintPosTriggerInUse) {
			const paymentRequestId = newFirestoreId();
			const responsiblePerson = getResponsiblePersonDetails(
				rentalInfo.responsiblePerson,
				rental.shoppers,
			);
			addOrderPaymentRequest({
				id: paymentRequestId,
				shopId: shop.shopInfo.id,
				orderId: rental.rentalInfo.id,
				amount: {
					value: toPay,
					currency: currency.code,
				},
				paymentNumber,
				shopperIds: [shopper.id],
				productIds: shopperProducts.map((p) => p.id),
				paid: false,
			});
			if (receiptBarCode) {
				handlePosIntegrationAction(
					{
						shopId: shop.shopInfo.id,
						shopReadOnly: shop.readOnly,
						locationId: rentalInfo.startLocation.id,
						customer: {
							firstName: responsiblePerson.firstName,
							lastName: responsiblePerson.lastName,
						},
						orderDate: rentalInfo.startDate,
						receiptProducts: shopperProducts,
						barCode: receiptBarCode,
						paymentNumber,
						paymentRequestId,
						categories,
					},
					t,
				);
			}
		}
		setPrintingShopperId(null);
		setPaymentNumber(null);
		setReceiptBarCode(null);
	};

	const handlePrintContentUpdate = async () => {
		const orderNumber =
			rentalInfo.orderNumber ??
			(await getNewOrderNumber({ orderId: rentalInfo.id, shopId: rentalInfo.shopId }));
		if (paymentNumber == null) {
			const newPaymentNumber = await getNewPaymentNumber(rentalInfo.shopId);
			setPaymentNumber(newPaymentNumber);
			setReceiptBarCode(
				getReceiptBarCode({
					readOnlyData: readOnly,
					shopInfo,
					paymentNumber: newPaymentNumber,
					amount: toPay,
					orderNumber: orderNumber,
					orderId,
					receiptType: 'shopper',
				}),
			);
		}
	};

	return (
		<>
			{editingCard && (
				<div className={classes.overlay} style={{ height: getShopperCardOverlayHeight() }} />
			)}
			<Card
				ref={shopperCardRef}
				classes={{
					root: activeShopper
						? classNames(classes.shopperCardSelected, {
								[classes.editingOverlay]: editingCard,
						  })
						: classes.shopperCard,
				}}
				onClick={() => handleCardClicked()}
			>
				<CardContent className={classes.cardContent}>
					<Grid container spacing={2} alignItems={'stretch'}>
						<Grid item xs={12} sm={12} md={4} lg={4} style={{ minWidth: 280 }} data-sl="mask">
							<ShopperInfo
								shopper={shopper}
								shopperIndex={shopperIndex}
								printReceipt={setPrintingShopperId}
								openDiscountDialog={() => setDiscountDialogOpen(true)}
								triggerDeleteShopper={() => setDeleteShopperPrompt(true)}
								openAdditionalInformationDialog={() => setAdditionalInformationDialogOpen(true)}
								editingShopper={editingShopper}
								editingPrice={editingPrice}
								setEditingShopper={(isEditing: boolean) => setEditingShopper(isEditing)}
							/>
						</Grid>
						<Grid item xs={12} sm={12} md={8} lg={8} style={{ flex: 1 }}>
							<ShopperProducts
								activeRental={rental}
								shopperProducts={shopperProducts}
								shopper={shopper}
								onProductsDelete={handleDeleteProducts}
								onShopperDelete={handleDeleteShopper}
								returnProducts={handleReturnProducts}
								productToSetHighlightFor={productToSetHighlightFor}
								currency={currency}
								editingShopper={editingShopper}
								editingPrice={editingPrice}
								onPriceEdit={(editing) => setEditingPrice(editing)}
							/>
						</Grid>
					</Grid>
				</CardContent>
				{renderSnackBar()}
				{scannedItem && scanObject.scannedCode && (
					<AddScannedProductDialog
						open={addScannedProductDialogOpen}
						scannedItem={scannedItem}
						stockProducts={stockProducts}
						shopper={shopper}
						shoppers={shoppers}
						activeRental={rental}
						shopPublicInfo={shopPublicInfo}
						scannedCode={scanObject.scannedCode}
						lang={lang}
						handleClose={() => handleCloseScannedProductDialog()}
						useDinVariation={!!shop.shopInfo.plugins?.din?.useDinVariation}
					/>
				)}
				<Snackbar
					anchorOrigin={{
						vertical: 'bottom',
						horizontal: 'center',
					}}
					classes={{
						root: classes.snackRoot,
					}}
					open={printingShopperId !== null}
					ContentProps={{
						'aria-describedby': 'message-id',
						classes: {
							root: classes.contentRoot,
							message: classes.contentMessage,
						},
					}}
					message={<span id="message-id">{t('printing', 'Printing')}</span>}
				/>
				<DeleteShopperDialog
					open={deleteShopperPrompt}
					onAccept={handleDeleteShopper}
					onCancel={cancelDeleteShopper}
				/>
				{discountDialogOpen && renderDiscountDialog()}
				{additionalInformationDialogOpen && (
					<ShopperCommentModal
						open={additionalInformationDialogOpen}
						onCancel={handleAdditionalInformationDialogClose}
						onEdit={(value) => handleAdditionalInformationFormChange(value)}
						value={shopper.additionalInformation}
						label={t('common:form.comments', 'Comments')}
					/>
				)}
				<ReactToPrint
					trigger={() => (
						<button id={`print-btn-${shopper.id}`} style={{ display: 'none' }}>
							{'Print'}
						</button>
					)}
					onBeforeGetContent={() => {
						handlePrintContentUpdate();
						return new Promise<void>((resolve) => setPrintContentPromise({ resolve }));
					}}
					content={() => receiptRef.current}
					onAfterPrint={() => handlePrintingDone(receiptBarCode, paymentNumber || 0)}
				/>
				<div style={{ display: 'none' }}>
					<ShopperReceipt
						rental={rental}
						shopperId={shopper.id}
						shop={shop.publicInfo}
						ref={receiptRef as any}
						barCode={receiptBarCode ?? getDefaultOrderBarcode(orderNumber, orderId)}
					/>
				</div>
			</Card>
		</>
	);
};

const styles = (theme: Theme) =>
	createStyles({
		snackBarRoot: {
			right: theme.spacing(3),
			[theme.breakpoints.down('sm')]: {
				right: theme.spacing(1),
			},
		},
		close: {
			padding: theme.spacing(0.5),
			position: 'absolute',
			top: '14px',
			right: '14px',
		},
		shopperCard: {
			boxSizing: 'border-box',
			marginTop: theme.spacing(2),
			marginBottom: theme.spacing(1),
			width: '100%',
			paddingTop: 0,
			'&:last-child': {
				marginRight: 0,
				paddingBottom: 0,
			},
			overflow: 'visible',
			border: 'solid 2px' + theme.palette.common.white,
		},
		shopperCardSelected: {
			boxSizing: 'border-box',
			marginTop: theme.spacing(2),
			marginBottom: theme.spacing(1),
			width: '100%',
			paddingTop: 0,
			'&:last-child': {
				marginRight: 0,
				paddingBottom: 0,
			},
			overflow: 'visible',
			border: 'solid 2px' + theme.palette.primary.main,
		},
		editingOverlay: {
			zIndex: 10,
			position: 'relative',
		},
		snack: {
			backgroundColor: theme.palette.error.main,
			color: theme.palette.error.contrastText,
			padding: theme.spacing(4),
			borderRadius: 4,
			width: '100%',
		},
		cardContent: {
			padding: 0,
			paddingBottom: '0px !important',
			margin: theme.spacing(3, 2, 3, 2),
		},
		flexGrow: {
			flexGrow: 1,
		},
		snackRoot: {
			width: `calc(100% - ${theme.spacing(2)})`,
			bottom: 0,
			marginBottom: theme.spacing(1),
		},
		contentRoot: {
			backgroundColor: theme.palette.info.darker,
			maxWidth: 'none',
			flexGrow: 1,
		},
		contentMessage: {
			margin: '0 auto',
		},
		overlay: {
			position: 'fixed',
			display: 'block',
			width: '100%',
			height: '100%',
			top: 0,
			left: 0,
			right: 0,
			bottom: 0,
			backgroundColor: 'rgba(0,0,0,0.5)',
			zIndex: 2,
		},
	});

const mapStateToProps = ({ shop, stock, lang, rentalView }: ReduxState): StateToProps => {
	return {
		stockProducts: stock.stockProducts.data!,
		categories: stock.categories.data!,
		shop: shop.shop.data!,
		lang,
		activeShopperId: rentalView.activeShopperId,
		selectedNonSetProductIds: rentalView.selectedNonSetProductIds,
		selectedSetProducts: rentalView.selectedSetProducts,
	};
};

export default withStyles(styles)(
	connect(mapStateToProps, null, null, { forwardRef: true })(ShopperCard),
);
